import { useEffect, useState } from "react";
import { Radio, Plus, Trash2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { DEMO_CHANNELS } from "./mdtDemo";
import { inputCls, btnCls, toastOk } from "./RosterTab";

/* Radio directory — the channels the in-game radio uses, listed for every unit.
   Supervisors (Sgt+) add/remove assignments. */

export default function RadioTab({ G }) {
  const { serverId, token, demo, sfx, me } = G;
  const [channels, setChannels] = useState(null);
  const [label, setLabel] = useState("");
  const [ch, setCh] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (demo) { setChannels(DEMO_CHANNELS); return; }
    api.get(`/nc/servers/${serverId}/cad/radio/channels`, { params: { token } })
      .then((r) => setChannels(r.data.channels)).catch(() => setChannels([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (label.trim().length < 2 || !ch.trim()) return;
    sfx.keyTick();
    if (demo) { setChannels((c) => [...c, { id: `l${Date.now()}`, label: label.trim().toUpperCase(), channel: ch.trim() }]); setLabel(""); setCh(""); return; }
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/cad/radio/channels`, { label: label.trim(), channel: ch.trim() }, { params: { token } });
      toastOk(r.data.message); setLabel(""); setCh(""); load();
    } catch (e) { import("sonner").then(({ toast }) => toast.error(e.response?.data?.detail || "Failed")); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    sfx.keyTick();
    if (demo) { setChannels((c) => c.filter((x) => x.id !== id)); return; }
    try { await api.delete(`/nc/servers/${serverId}/cad/radio/channels/${id}`, { params: { token } }); load(); } catch { /* next load */ }
  };

  if (!channels) return <div className="flex justify-center p-8"><Loader2 className="w-4 h-4 animate-spin text-sky-400" /></div>;

  return (
    <div className="p-3.5 flex flex-col gap-2" data-testid="radio-tab">
      <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-1.5">
        <Radio className="w-3 h-3 text-emerald-400" /> Active radio assignments — dial these on your in-game radio
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {channels.map((c) => (
          <div key={c.id} data-testid={`channel-${c.channel}`} className="border border-emerald-500/25 bg-emerald-500/[0.04] p-2.5 relative group">
            <p className="font-mono text-lg font-bold text-emerald-300 leading-none">CH {c.channel}</p>
            <p className="font-mono text-[8.5px] uppercase tracking-[0.15em] text-zinc-300 mt-1">{c.label}</p>
            {c.updated_by && <p className="font-mono text-[7px] text-zinc-600 uppercase mt-0.5">set by {c.updated_by}</p>}
            {me.is_supervisor && (
              <button data-testid={`channel-del-${c.channel}`} onClick={() => remove(c.id)}
                className="absolute top-1.5 right-1.5 text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3 h-3" /></button>
            )}
          </div>
        ))}
      </div>
      {me.is_supervisor ? (
        <div className="flex gap-1.5 mt-1">
          <input data-testid="channel-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. SWAT / TAC)" className={`${inputCls} flex-1 min-w-0`} />
          <input data-testid="channel-num" value={ch} onChange={(e) => setCh(e.target.value)} placeholder="CH #" className={`${inputCls} w-20`} />
          <button data-testid="channel-add" onClick={add} disabled={busy || label.trim().length < 2 || !ch.trim()}
            className={`${btnCls} border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-1`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Assign
          </button>
        </div>
      ) : (
        <p className="font-mono text-[8px] text-zinc-600 uppercase tracking-[0.15em]">Supervisors (Sgt+) manage channel assignments.</p>
      )}
    </div>
  );
}
